import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useViewSingleProduct } from "../../hooks/products/viewSingleProduct";
import { useViewIndustryQuery } from "../../hooks/industry/industryList";
import { useViewCategory } from "../../hooks/categorys/category.showall";
import "../../assets/styles/products.css";
import { ArrowLeft } from "lucide-react";
import Button from "../../components/common/button";
import { useRemoveProduct } from "../../hooks/products/deleteProduct";
import { toast } from "react-hot-toast";
import ProductModal from "../../components/modal/productModal";
import Modal from "../../components/common/modal";
import { getPageTitle } from "../../utils/getPageTitle";
import FallbackImage from "../../components/common/fallbackImage";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [mode, setMode] = useState("view");
  const [modalKey, setModalKey] = useState(0);
  const {
    data: rawProductData,
    isLoading,
    isError,
    error,
  } = useViewSingleProduct(id);
  const { data: industriesData } = useViewIndustryQuery();
  const { data: categoriesData } = useViewCategory();

  const industriesList = React.useMemo(() => {
    if (!industriesData) return [];
    const list =
      industriesData?.data?.industry ||
      industriesData?.data?.industries ||
      industriesData?.data ||
      industriesData?.industry ||
      industriesData?.industries ||
      industriesData;
    return Array.isArray(list) ? list : [];
  }, [industriesData]);

  const categoriesList = React.useMemo(() => {
    if (!categoriesData) return [];
    const list =
      categoriesData?.data?.category ||
      categoriesData?.data?.categories ||
      categoriesData?.data ||
      categoriesData?.category ||
      categoriesData?.categories ||
      categoriesData;
    return Array.isArray(list) ? list : [];
  }, [categoriesData]);

  const product = React.useMemo(() => {
    if (!rawProductData) return null;
    return (
      rawProductData?.data?.product ||
      rawProductData?.product ||
      rawProductData?.data?.data ||
      rawProductData?.data ||
      rawProductData
    );
  }, [rawProductData]);

  const { mutate: removeProduct, isPending: isDeleting } = useRemoveProduct();

  const location = useLocation();

  const handleDelete = () => {
    setMode("delete");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  if (isLoading)
    return <div className="product-page">Loading product details...</div>;
  if (isError) {
    console.error("Error loading product:", error);
    return (
      <div className="product-page">
        Error loading product details. Please try again later.
      </div>
    );
  }
  if (!product) return <div className="product-page">Product not found.</div>;

  const resolveUrl = (path) => resolveImageUrl(path);

  const getThumbnailSource = () =>
    product.thumbnail_image ||
    product.thumbnail ||
    product.image ||
    product.media?.thumbnail ||
    product.media?.thumbnail_image ||
    product.media?.images?.[0] ||
    product.images?.[0];

  const renderThumbnail = () => resolveUrl(getThumbnailSource());

  const getEmbedUrl = (url) => {
    if (
      typeof url === "string" &&
      (url.includes("youtube.com") || url.includes("youtu.be"))
    ) {
      let videoId = "";
      if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const getChipClass = (index) => {
    const classes = [
      "chip-purple",
      "chip-blue",
      "chip-green",
      "chip-yellow",
      "chip-red",
      "chip-pink",
      "chip-cyan",
      "chip-orange",
      "chip-teal",
    ];
    return `chip ${classes[index % classes.length]}`;
  };

  const getIndustryTitle = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return "-";

    const items = (Array.isArray(val) ? val : [val]).filter(Boolean);
    if (items.length === 0) return "-";

    return items
      .map((item) => {
        // If it's already an object with title/name, use it
        if (typeof item === "object" && (item?.title || item?.name)) {
          return item.title || item.name;
        }

        const id = typeof item === "string" ? item : item?._id || item?.id;
        const found = industriesList.find(
          (i) => String(i._id || i.id) === String(id),
        );
        if (found) return found.title || found.name || found.label;
        if (typeof item === "object")
          return (
            item?.title ||
            item?.name ||
            item?.label ||
            item?.industryName ||
            item?.industry_name ||
            "-"
          );
        return item;
      })
      .join(", ");
  };

  const getCategoryTitle = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return "-";

    const items = (Array.isArray(val) ? val : [val]).filter(Boolean);
    if (items.length === 0) return "-";

    return items
      .map((item) => {
        // If it's already an object with title/name, use it
        if (typeof item === "object" && (item?.title || item?.name)) {
          return item.title || item.name;
        }

        const id = typeof item === "string" ? item : item?._id || item?.id;
        const found = categoriesList.find(
          (c) => String(c._id || c.id) === String(id),
        );
        if (found) return found.title || found.name || found.label;
        if (typeof item === "object")
          return (
            item?.title ||
            item?.name ||
            item?.label ||
            item?.categoryName ||
            item?.category_name ||
            "-"
          );
        return item;
      })
      .join(", ");
  };

  return (
    <>
      <h5>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back to Companies
        </button>
      </h5>

      <div className="page-body">
        <div className="page-header">
          {/* <h1>{product.title} Product Details</h1> */}
        </div>

        <div className="viewproducts">
          <button className="share-btn">Share</button>
          <button className="delete-btn" onClick={handleDelete}>
            Delete
          </button>
          <button
            className="edit-btn"
            onClick={() => navigate(`/products/edit/${id}`)}
          >
            Edit Product
          </button>
        </div>
      </div>
      {/* <div className="page-body">
        <div className="page-header">
          <h1>Product Details</h1>

          <div className="header-actions">
            <button className="share-btn">Share</button>
            <button className="delete-btn" onClick={handleDelete}>
              Delete
            </button>
            <button className="edit-btn">Edit Product</button>
          </div>
        </div>
      </div> */}

      <div className="product-page">
        <div className="details-layout">
          {/* LEFT SIDEBAR */}
          <div className="left-panel">
            <div className="card">
              <FallbackImage
                src={getThumbnailSource()}
                alt={product.title}
                className="main-image"
              />

              <div className="thumb-row">
                {getThumbnailSource() && (
                  <FallbackImage
                    src={getThumbnailSource()}
                    alt="thumb-main"
                    className="thumb-image active"
                  />
                )}
                {(product.media?.images || product.images || []).map(
                  (img, i) => (
                    <FallbackImage
                      key={i}
                      src={img}
                      alt={`thumb-${i}`}
                      className="thumb-image"
                    />
                  ),
                )}
              </div>
            </div>

            <div className="stats-grid">
              <div className="stats-card">
                <h3 style={{ color: "green" }}>
                  {product.analytics?.views || 0}
                </h3>
                <p style={{ color: "green" }}>Views</p>
              </div>

              <div className="stats-card">
                <h3 style={{ color: "blue" }}>
                  {product.analytics?.downloads || 0}
                </h3>
                <p style={{ color: "blue" }}>Downloads</p>
              </div>

              <div className="stats-card">
                <h3 style={{ color: "orange" }}>
                  {product.analytics?.share || 0}
                </h3>
                <p style={{ color: "orange" }}>Shares</p>
              </div>
            </div>

            {/* TAGS */}
            <div className="card" style={{ marginTop: "20px" }}>
              <h3>Tags</h3>

              <div className="chip-container">
                {product.tags?.map((tag, index) => (
                  <span className={getChipClass(index)} key={index}>
                    {typeof tag === "object" ? tag.name || tag.title : tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="right-panel">
            {/* PRODUCT INFO */}
            <div className="card">
              <div className="top-row">
                <div>
                  <h2>{product.title}</h2>
                  <p className="subtitle">{product.subTitle}</p>
                </div>

                <span className="status-badge">{product.status}</span>
              </div>

              <div className="info-grid">
                <div>
                  <label>Industries</label>
                  <span className="value">
                    {getIndustryTitle(
                      product?.industries ||
                        product?.industry ||
                        product?.industry_id ||
                        product?.industryId ||
                        product?.industryName ||
                        product?.industry_name ||
                        product?.industry_info ||
                        product?.industryData,
                    )}
                  </span>
                </div>

                <div>
                  <label>category</label>
                  <span className="value">
                    {getCategoryTitle(
                      product?.category ||
                        product?.categories ||
                        product?.category_id ||
                        product?.categoryId ||
                        product?.categoryName ||
                        product?.category_name ||
                        product?.category_info ||
                        product?.categoryData,
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* DESCRIPTION */}
            <div className="card">
              <h3>Description</h3>

              <p className="description">{product.description}</p>
            </div>
            {/* 
</div>

        <div className="details-layout"> */}

            {/* SPECIFICATIONS */}
            {/* <div className="card">
              <h3>Specifications</h3>

              <div className="spec-grid">
                <div>
                  <span>Slug</span>
                  <strong>{product.slug}</strong>
                </div>

                <div>
                  <span>Status</span>
                  <strong>{product.status}</strong>
                </div>

                <div>
                  <span>Created</span>
                  <strong>
                    {new Date(product.createdAt).toLocaleDateString()}
                  </strong>
                </div>

                <div>
                  <span>Updated</span>
                  <strong>
                    {new Date(product.updatedAt).toLocaleDateString()}
                  </strong>
                </div>
              </div>
            </div> */}

            {/* INDUSTRIES */}

            {/* VIDEOS */}
            {(() => {
              const allVideos = [
                ...(product.videos || []),
                ...(product.media?.videos || []),
              ];
              if (allVideos.length === 0) return null;

              return (
                <div className="card">
                  <h3>Product Videos ({allVideos.length})</h3>
                  <div
                    className="video-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: "15px",
                      marginTop: "15px",
                    }}
                  >
                    {allVideos.map((vid, index) => {
                      const isUrl = typeof vid === "string";
                      const videoUrl = isUrl ? vid : vid.url || vid.path || vid.file;
                      const thumbUrl = isUrl ? null : vid.thumbnail_image;
                      const title = isUrl ? "" : vid.title || vid.name;
                      const description = isUrl ? "" : vid.description;

                      return (
                        <div
                          key={index}
                          className="video-item"
                          onClick={() => {
                            setSelectedVideo(videoUrl);
                            setIsVideoModalOpen(true);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <div
                            className="video-preview"
                            style={{ height: "150px" }}
                          >
                            <FallbackImage
                              src={thumbUrl || getThumbnailSource()}
                              alt={`video-${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <div className="play-btn">▶</div>
                          </div>
                          {title && (
                            <h4
                              style={{
                                marginTop: "10px",
                                fontSize: "14px",
                                fontWeight: "600",
                              }}
                            >
                              {title}
                            </h4>
                          )}
                          {description && (
                            <p
                              style={{
                                fontSize: "12px",
                                color: "#666",
                                marginTop: "4px",
                                display: "-webkit-box",
                                WebkitLineClamp: "2",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            {/* CATALOGS */}
            {(
              product.media?.catalogs ||
              product.media?.catlogs ||
              product.catalogs ||
              product.catlogs ||
              []
            ).length > 0 && (
              <div className="card">
                <h3>
                  Documents (
                  {
                    (
                      product.media?.catalogs ||
                      product.media?.catlogs ||
                      product.catalogs ||
                      product.catlogs ||
                      []
                    ).length
                  }
                  )
                </h3>

                <div className="catalog-list" style={{ marginTop: "15px" }}>
                  {(
                    product.media?.catalogs ||
                    product.media?.catlogs ||
                    product.catalogs ||
                    product.catlogs ||
                    []
                  ).map((cat, index) => {
                    const fileName =
                      typeof cat === "string"
                        ? cat.split("/").pop()
                        : cat.name || cat.title || `Document ${index + 1}`;

                    const fileUrl =
                      typeof cat === "string"
                        ? cat
                        : cat.url || cat.path || cat.file;

                    return (
                      <div
                        key={index}
                        className="catalog-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px",
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          className="file-icon"
                          style={{
                            fontSize: "24px",
                            marginRight: "15px",
                          }}
                        >
                          📄
                        </div>

                        <div className="file-info" style={{ flex: 1 }}>
                          <div
                            className="file-name"
                            style={{ fontWeight: "500" }}
                          >
                            {fileName}
                          </div>
                        </div>

                        <Button
                          href={resolveUrl(fileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="CommonButton"
                        >
                          View
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ProductModal
        key={modalKey}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setMode("view");
        }}
        editProduct={product}
        mode={mode}
        onSubmit={() => {
          if (mode === "delete") {
            const productId = product?._id || product?.id;
            removeProduct(productId, {
              onSuccess: () => {
                toast.success("Product deleted successfully!");
                setIsModalOpen(false);
                navigate("/products");
              },
              onError: () => {
                toast.error("Failed to delete product. Please try again.");
              },
            });
          }
        }}
        isLoading={isDeleting}
      />

      <Modal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        className="video-preview-modal"
      >
        <div className="video-modal-wrapper">
          {selectedVideo &&
            (() => {
              const embedUrl = getEmbedUrl(selectedVideo);
              const fullUrl = resolveUrl(selectedVideo);

              return embedUrl ? (
                <iframe
                  width="100%"
                  height="500px"
                  src={embedUrl}
                  title="Video Preview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  width="100%"
                  height="auto"
                  style={{ maxHeight: "80vh" }}
                  controls
                  autoPlay
                >
                  <source src={fullUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              );
            })()}
        </div>
      </Modal>
    </>
  );
}

export default ProductDetailsPage;
